/* eslint-disable max-lines -- detail page with tab strip + dialogs; split would obscure orchestration flow. calendar-offer-bound-reservations adds calendar reserve state. */
/* eslint-disable max-lines-per-function -- calendar-offer-bound-reservations adds calendar reserve hooks + state to detail page */
import type { ResolvedOffer } from '@ooh/screens';
import * as React from 'react';
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
  useRouteContext,
  useSearch,
} from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { AVAILABILITY_CALENDAR_CAPABILITY, isNotFoundError } from '@ooh/contracts';
import { MEDIA_UNIT_AVAILABILITY_KEY_PREFIX } from '../../../lib/query-keys';
import { MediaUnitDetailBackLink } from '../../../features/media-units/components/media-unit-detail-back-link';
import { decodeCatalogSearch } from '../../../lib/catalog-return-context';
import { PageTitleContext } from '../../__root';
import { mediaUnitDetailQuery } from '../../../features/media-units/api/queries';
import { MediaUnitNotFoundError } from '../../../features/media-units/components/media-unit-not-found';
import { ErrorFallback } from '../../../components/error-fallback';
import { useHasPermission } from '../../../hooks/use-has-permission';
import {
  DetailSkeleton,
  MediaUnitDetailView,
  type MultiSelectCellRef,
  type OpenFullQueueSearch,
  getInitialSelectedDraftId,
  resolveAvailabilityMultiSelectCell,
  resolveFreeCellDateRange,
  resolvePreselectedOfferId,
  buildQueueJoinPanelLabels,
  normalizeClientList,
  QuickAddOfferPicker,
  QuickAddOfferPickerCompactTrigger,
  buildQuickAddPickerLabels,
  useDetailAvailability,
  useQueueClientOptions,
  useSelectBatchResults,
  useSelectQueueJoin,
  UnitReservationsPanel,
  INVENTORY_QUICK_ADD_PICKER_BASE_KEY,
  useCalendarOfferPicker,
  useQuickAddPickerData,
  useQueueOfferOptions,
} from '@ooh/screens';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { UnifiedSelectPopover, type UnifiedSelectCellResolution } from '@ooh/screens';
import { useMultiSelectAnchor } from '@ooh/screens';
import { useClientsList } from '../../../features/clients/api';
import { useCampaignNumberPreview, useOfferSalesUsers } from '../../../features/offers/api';
import { useQuickAddPipeline } from '../../../features/media-units/components/use-quick-add-pipeline';
import { ArrowLeft, PlusSquare } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ooh/ui/components/tooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@ooh/ui/components/tabs';
import type { CampaignNumberPreviewQuery } from '@ooh/contracts';

// Re-exported for use in test wrappers
export { MediaUnitNotFoundError };

// ─── Search schema ────────────────────────────────────────────

const MediaUnitDetailSearchSchema = z
  .object({
    tab: z.enum(['overview', 'reservations']).default('overview').catch('overview'),
    focus: z.string().optional(),
    /**
     * Opaque, serialized catalog filter/sort state forwarded from the `/media-units`
     * row link. See `catalog-return-context.ts`. CU-86cajbuug.
     */
    catalogSearch: z.string().optional(),
    /**
     * Offer id forwarded from the offer units-tab row link. When present, the page
     * renders a "back to offer" link (`/offers/:offerId`) instead of the default
     * "back to catalog" link. CU-86cbc3qx6.
     */
    offerId: z.string().optional(),
  })
  .strict();

type MediaUnitDetailSearch = z.infer<typeof MediaUnitDetailSearchSchema>;

// ─── Route definition ─────────────────────────────────────────

function MediaUnitErrorComponent({ error, reset }: ErrorComponentProps) {
  // Safe to read search here per TanStack Router's error-boundary hooks
  // (useParams/useSearch/useRouteContext are always available in errorComponent,
  // unlike useLoaderData). Forwards the catalog filters so "back to catalog"
  // still restores them when the unit itself 404s. CU-86cb1nq9n.
  const search = useSearch({ from: '/media-units/$id/' }) as MediaUnitDetailSearch;
  if (isNotFoundError(error)) {
    return <MediaUnitNotFoundError catalogSearch={search.catalogSearch} />;
  }
  return <ErrorFallback error={error} reset={reset} />;
}

export const Route = createFileRoute('/media-units/$id/')({
  validateSearch: MediaUnitDetailSearchSchema,
  component: MediaUnitDetailPage,
  errorComponent: MediaUnitErrorComponent,
});

// ─── Page component ───────────────────────────────────────────

// eslint-disable-next-line complexity -- tab-strip orchestrator; hooks + early-returns account for complexity
export function MediaUnitDetailPage() {
  const { t } = useTranslation('reservations');
  const { t: tInventory } = useTranslation('inventory');
  const { id } = useParams({ from: '/media-units/$id/' });
  const { auth } = useRouteContext({ from: '__root__' });
  const navigate = useNavigate({ from: Route.fullPath });
  const { setPageTitle } = React.useContext(PageTitleContext);
  const search = useSearch({ from: '/media-units/$id/' }) as MediaUnitDetailSearch;
  const canViewCosts = useHasPermission('cost.view');
  const canQuickAddToOffer = useHasPermission('offer.prepare');
  const canAssignOtherSalesperson = useHasPermission('offer.assign_other_salesperson');
  const canCreateClient = useHasPermission('client.create');
  const canViewAvailabilityCalendar = useHasPermission(AVAILABILITY_CALENDAR_CAPABILITY);

  const unitId = Number(id);

  // Media unit detail
  const { data, isLoading, isError, error } = useQuery(mediaUnitDetailQuery(auth, id));

  const queueOfferOptions = useQueueOfferOptions();
  const availability = useDetailAvailability({
    unitId,
    city: data?.data.city,
    canViewCalendar: canViewAvailabilityCalendar,
    queueOfferOptions,
  });
  const { periods: detailAvailabilityPeriods, entriesGrid: detailEntriesGrid } = availability;

  // Offer picker data (needed by QuickAddOfferPicker for calendar reserve flow)
  const { draftsData, lastUsedData } = useQuickAddPickerData();
  const clientsQuery = useClientsList();
  const salesUsersQuery = useOfferSalesUsers();
  const initialSelectedDraftId = getInitialSelectedDraftId(draftsData, lastUsedData);
  const [selectedDraftId, setSelectedDraftId] = React.useState<string | null>(
    initialSelectedDraftId,
  );
  const drafts = draftsData?.drafts ?? [];
  // Auto-applies the preselected draft to the SELECT popover's Rezerwacja/Opcja actions
  // with no extra confirm click — only overridden once the user actively picks a
  // different offer via the modal (which sets `chosenOffer` explicitly).
  const preselectedOfferId = React.useMemo(
    () => resolvePreselectedOfferId(drafts, selectedDraftId),
    [drafts, selectedDraftId],
  );
  const clientOptions = React.useMemo(
    () =>
      normalizeClientList(clientsQuery.data).map((c) => ({
        label: c.name,
        value: String(c.id),
        brands: c.brands,
        acronym: c.acronym,
      })),
    [clientsQuery.data],
  );
  const salesUsers = React.useMemo(() => salesUsersQuery.data ?? [], [salesUsersQuery.data]);
  // See media-units/index.tsx's identical comment: QuickAddOfferPicker
  // reports its "creating" mode selection here rather than calling
  // useCampaignNumberPreview itself (offer-draft-numbering / "Campaign-number
  // preview on offer creation forms").
  const [campaignNumberPreviewInput, setCampaignNumberPreviewInput] =
    React.useState<CampaignNumberPreviewQuery | null>(null);
  const campaignNumberPreview = useCampaignNumberPreview(campaignNumberPreviewInput);
  // I/O boundary: buildQuickAddPickerLabels passes string keys from a fixed external namespace;
  // the typed TFunction only accepts enumerated keys, so a narrow cast is required here.
  const translatePickerLabel = React.useCallback(
    (key: string): string => (tInventory as (k: string) => string)(key),
    [tInventory],
  );
  const pickerLabels = React.useMemo(
    () => buildQuickAddPickerLabels(translatePickerLabel, INVENTORY_QUICK_ADD_PICKER_BASE_KEY),
    [translatePickerLabel],
  );

  React.useEffect(() => {
    setSelectedDraftId(initialSelectedDraftId);
  }, [initialSelectedDraftId]);

  // ─── Header "Dodaj do oferty" flow ─────────────────────────
  const [isHeaderPickerOpen, setIsHeaderPickerOpen] = React.useState(false);
  const {
    confirm: headerQuickAddConfirm,
    openFullForm: headerOpenFullForm,
    navigateToNewOffer: headerNavigateToNewOffer,
  } = useQuickAddPipeline();

  // Multi-select SELECT/INSPECT flow state (task 4.4, unified-availability-interaction)
  const {
    multiSelection,
    anchorRef: multiSelectAnchorRef,
    anchorVersion: multiSelectAnchorVersion,
    updateSelection: updateMultiSelectAnchor,
    narrowSelection: narrowMultiSelection,
  } = useMultiSelectAnchor('cell');
  const [chosenOffer, setChosenOffer] = React.useState<ResolvedOffer | null>(null);
  // Opens the SELECT popover's own "which offer" picker as a real modal (see
  // QuickAddOfferPickerCompactTrigger) instead of embedding a second, cramped copy of
  // QuickAddOfferPicker inline in the popover.
  const [isSelectOfferPickerOpen, setIsSelectOfferPickerOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const canManageReservations = useHasPermission('reservation.manage');

  const clearMultiSelection = React.useCallback(() => {
    updateMultiSelectAnchor(null);
    setChosenOffer(null);
  }, [updateMultiSelectAnchor]);

  const { handleCalendarPickerConfirm } = useCalendarOfferPicker({
    closeCalendarPicker: () => setIsSelectOfferPickerOpen(false),
    setCalendarResolvedOffer: setChosenOffer,
  });

  const detailQueueClientOptions = useQueueClientOptions();
  const detailQueueJoinPanelLabels = React.useMemo(
    () =>
      buildQueueJoinPanelLabels(t as (key: string, options?: Record<string, unknown>) => string),
    [t],
  );
  const resolveMultiSelectCell = React.useCallback(
    (ref: MultiSelectCellRef): UnifiedSelectCellResolution | null =>
      resolveAvailabilityMultiSelectCell(ref, detailAvailabilityPeriods, detailEntriesGrid),
    [detailAvailabilityPeriods, detailEntriesGrid],
  );

  const {
    queueJoinSection: detailSelectQueueJoinSection,
    queueJoinLoading: isDetailSelectQueueLoading,
  } = useSelectQueueJoin({
    selection: multiSelection,
    resolveCell: resolveMultiSelectCell,
    testId: 'detail-select-queue',
    clientOptions: detailQueueClientOptions,
    offerOptions: queueOfferOptions,
    labels: detailQueueJoinPanelLabels,
  });

  // Defaults the calendar-driven "creating"-mode offer picker's campaign-duration
  // field to the exact month/period the caller just selected (CU-86cb8grgr).
  const calendarPickerDefaultRange = React.useMemo(
    () => resolveFreeCellDateRange(multiSelection, resolveMultiSelectCell),
    [multiSelection, resolveMultiSelectCell],
  );

  const refreshAvailability = React.useCallback(
    () => void queryClient.invalidateQueries({ queryKey: MEDIA_UNIT_AVAILABILITY_KEY_PREFIX }),
    [queryClient],
  );
  const { handleBatchReserveSuccess, handleBatchReleaseSuccess } = useSelectBatchResults({
    periods: detailAvailabilityPeriods,
    narrowSelection: narrowMultiSelection,
    resolveCell: resolveMultiSelectCell,
    refreshAvailability,
  });

  // "Otwórz pełną kolejkę" (CU-86cb7ctfw) deep-links to the same reservation's queue on the
  // global /reservations list, pre-scoped to this unit + term.
  const handleOpenFullQueue = React.useCallback(
    (fullQueueSearch: OpenFullQueueSearch) => {
      void navigate({ to: '/reservations', search: fullQueueSearch });
    },
    [navigate],
  );

  // Tab navigation
  const handleTabChange = React.useCallback(
    (tab: string) => {
      void navigate({
        replace: true,
        search: (prev) => ({
          ...prev,
          tab: tab as MediaUnitDetailSearch['tab'],
        }),
      });
    },
    [navigate],
  );

  if (isLoading) return <DetailSkeleton />;
  if (isError) throw error;
  if (!data) return null;

  const headerActions = canQuickAddToOffer ? (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={tInventory('quickAdd.rowActionLabel')}
            data-testid="detail-header-quick-add-btn"
            onClick={() => setIsHeaderPickerOpen(true)}
          >
            <PlusSquare className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{tInventory('quickAdd.rowActionLabel')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null;

  // Overrides the default "back to catalog" link when reached from an offer's units
  // tab identifier-cell link (see units-tab-columns.tsx), so the user can navigate
  // back to the offer instead of the catalog. CU-86cbc3qx6.
  const backLink =
    search.offerId != null ? (
      <Link
        to="/offers/$offerId"
        params={{ offerId: search.offerId }}
        search={{ tab: 'units' }}
        className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
        data-testid="back-link"
      >
        <ArrowLeft className="h-4 w-4" />
        {tInventory('detail.clientDetail.backToOffer')}
      </Link>
    ) : (
      <MediaUnitDetailBackLink catalogSearch={search.catalogSearch} />
    );

  return (
    <Tabs value={search.tab} onValueChange={handleTabChange} className="mx-auto w-full max-w-6xl">
      <TabsList className="mt-6 ml-6">
        <TabsTrigger value="overview" data-testid="tab-overview">
          {t('tabs.overview')}
        </TabsTrigger>
        <TabsTrigger value="reservations" data-testid="tab-reservations">
          {t('tabs.reservations')}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="overview">
        <MediaUnitDetailView
          unit={data.data}
          canViewCosts={canViewCosts}
          {...availability.viewProps}
          multiSelect={canManageReservations}
          multiSelection={multiSelection}
          onMultiSelectionChange={canManageReservations ? updateMultiSelectAnchor : undefined}
          headerActions={headerActions}
          catalogSearch={search.catalogSearch}
          backLink={backLink}
          onDeleted={() =>
            void navigate({
              to: '/media-units',
              search: decodeCatalogSearch(search.catalogSearch),
            })
          }
          onPageTitleChange={setPageTitle}
        />
        {/* Unified SELECT popover (task 4.4) — renders for the active multi-cell selection,
            which the grid already filters to SELECT-eligible cells (free/option-own/reserved-own). */}
        {multiSelection && multiSelection.cells.length > 0 ? (
          <UnifiedSelectPopover
            selection={multiSelection}
            resolveCell={resolveMultiSelectCell}
            anchorRef={multiSelectAnchorRef}
            anchorVersion={multiSelectAnchorVersion}
            // The detail card (~195px) is shorter than the offer-picker popover it
            // renders below it (~225px) — pin below so it never flips up over the
            // calendar/legend/card title (CU-86cbh0jhy).
            pinBelowAnchor
            offerPicker={
              <QuickAddOfferPickerCompactTrigger
                drafts={drafts}
                selectedDraftId={selectedDraftId}
                draftUnitLabel={pickerLabels.draftUnitLabel}
                draftSummarySeparator={pickerLabels.draftSummarySeparator}
                changeSelectionLabel={pickerLabels.changeSelectionLabel}
                pickOfferLabel={tInventory('quickAdd.picker.pickOfferLabel')}
                onOpenPicker={() => setIsSelectOfferPickerOpen(true)}
              />
            }
            chosenOfferId={chosenOffer?.id ?? preselectedOfferId}
            suppressOutsideDismiss={isSelectOfferPickerOpen}
            onClear={clearMultiSelection}
            onBatchReserveSuccess={handleBatchReserveSuccess}
            onBatchReleaseSuccess={handleBatchReleaseSuccess}
            resolveMediaUnitLabel={() => data.data.grwId}
            queueJoinSection={detailSelectQueueJoinSection}
            queueJoinLoading={isDetailSelectQueueLoading}
          />
        ) : null}
        {isSelectOfferPickerOpen ? (
          <QuickAddOfferPicker
            {...pickerLabels}
            presentation="modal"
            initialMode="changing"
            mediaUnitIds={[]}
            subtitle={tInventory('quickAdd.picker.subtitle')}
            drafts={drafts}
            selectedDraftId={selectedDraftId}
            onSelectDraft={setSelectedDraftId}
            clientOptions={clientOptions}
            salesUsers={salesUsers}
            authUserId={auth.user?.id}
            canAssignOtherSalesperson={canAssignOtherSalesperson}
            canCreateClient={canCreateClient}
            campaignNumberPreview={campaignNumberPreview}
            onCampaignNumberPreviewInputChange={setCampaignNumberPreviewInput}
            createDraftMode="inline"
            initialDefaultDateFrom={calendarPickerDefaultRange?.from ?? null}
            initialDefaultDateTo={calendarPickerDefaultRange?.to ?? null}
            confirmExistingLabel={tInventory('quickAdd.picker.confirmExistingLabel')}
            confirmNewLabel={tInventory('quickAdd.picker.confirmNewLabel')}
            isCalendarSelectionContext
            onConfirm={handleCalendarPickerConfirm}
            onCancel={() => setIsSelectOfferPickerOpen(false)}
            onOpenFullForm={(args) => {
              setIsSelectOfferPickerOpen(false);
              clearMultiSelection();
              // This picker instance is scoped to the current unit's own calendar
              // (mediaUnitIds={[]} above is unused by handleCalendarPickerConfirm) —
              // the media unit to carry is always this page's unitId (CU-86cb8grgn).
              headerOpenFullForm({ ...args, mediaUnitIds: [unitId] });
            }}
          />
        ) : null}
        {/* Header "Dodaj do oferty" picker — uses addUnitsToOffer flow (not createOffer) */}
        {isHeaderPickerOpen ? (
          <QuickAddOfferPicker
            {...pickerLabels}
            presentation="modal"
            mediaUnitIds={[unitId]}
            subtitle={tInventory('quickAdd.subtitle', { count: 1 })}
            drafts={drafts}
            selectedDraftId={selectedDraftId}
            onSelectDraft={setSelectedDraftId}
            clientOptions={clientOptions}
            salesUsers={salesUsers}
            authUserId={auth.user?.id}
            canAssignOtherSalesperson={canAssignOtherSalesperson}
            canCreateClient={canCreateClient}
            campaignNumberPreview={campaignNumberPreview}
            onCampaignNumberPreviewInputChange={setCampaignNumberPreviewInput}
            createDraftMode="inline"
            confirmExistingLabel={tInventory('quickAdd.confirmExistingWithCount', { count: 1 })}
            confirmNewLabel={tInventory('quickAdd.confirmNewWithCount', { count: 1 })}
            onConfirm={headerQuickAddConfirm}
            onNavigateToNewOffer={headerNavigateToNewOffer}
            onSuccess={() => setIsHeaderPickerOpen(false)}
            onCancel={() => setIsHeaderPickerOpen(false)}
            onOpenFullForm={(args) => {
              setIsHeaderPickerOpen(false);
              headerOpenFullForm(args);
            }}
          />
        ) : null}
      </TabsContent>

      <TabsContent value="reservations" className="p-6">
        <UnitReservationsPanel
          unitId={unitId}
          mediaUnitName={data.data.grwId}
          focus={search.focus ?? null}
          onOpenFullQueue={handleOpenFullQueue}
        />
      </TabsContent>
    </Tabs>
  );
}
